import { EmailHistory } from '../email/EmailHistory';
import AccountBalanceWalletRounded from '@mui/icons-material/AccountBalanceWalletRounded';
import BlockRounded from '@mui/icons-material/BlockRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import CurrencyExchangeRounded from '@mui/icons-material/CurrencyExchangeRounded';
import PrintRounded from '@mui/icons-material/PrintRounded';
import SavingsRounded from '@mui/icons-material/SavingsRounded';
import SwapHorizRounded from '@mui/icons-material/SwapHorizRounded';
import { Alert, Box, Button, Link, Stack, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';
import { api } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { Money } from '../../components/Money';
import { StatusChip } from '../../components/StatusChip';
import { businessDate, dateTime, money } from '../../lib/format';
import { paymentDoc, WhatsappA4, WhatsappHistory } from '../whatsapp';
import { AttachmentsPanel } from '../attachments';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import { percent } from './allocation';
import { AllocationsTable } from './kit/AllocationsTable';
import { idempotency, invalidateMoney, keys, unwrapPayment } from './kit/hooks';
import { links, METHOD_KINDS, PURPOSES } from './kit/labels';
import { METHOD_ICONS } from './kit/MethodSelect';
import { ReallocateDialog } from './kit/ReallocateDialog';
import { ReasonDialog } from './kit/ReasonDialog';
import {
  AllocationBar,
  BackLink,
  DetailSkeleton,
  ErrorState,
  InfoGrid,
  SectionCard,
  StatCard,
  StatGrid,
} from './kit/ui';
import { PurposeChip } from './PaymentsPage';
import { RefundDialog } from './RefundDialog';
import { MoreMenu } from '../../components/MoreMenu';
import { linkedAllocations } from '../../components/useLinked';

export function usePayment(id: string) {
  return useQuery({
    queryKey: keys.payment(id),
    queryFn: () => unwrapPayment(api.GET('/payments/{id}', { params: { path: { id } } })),
  });
}

export function PaymentDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const { format } = useShop();
  const navigate = useNavigate();
  const notify = useNotify();
  const qc = useQueryClient();
  const payment = usePayment(id);
  const [dialog, setDialog] = useState<'void' | 'reallocate' | 'refund' | null>(null);

  const voidMut = useMutation({
    mutationFn: (reason: string) =>
      unwrapPayment(
        api.POST('/payments/{id}/void', {
          params: { path: { id }, ...idempotency() },
          body: { reason, version: payment.data?.version ?? 0 },
        }),
      ),
    onSuccess: async (p) => {
      notify.success(`${p.number} voided`);
      await invalidateMoney(qc);
    },
    onError: (e) => notify.error(e),
  });

  if (payment.isLoading) return <DetailSkeleton />;
  if (payment.isError || !payment.data)
    return (
      <>
        <BackLink to="/payments" label="Payments" />
        <ErrorState error={payment.error} onRetry={() => void payment.refetch()} />
      </>
    );

  const p = payment.data;
  const meta = PURPOSES[p.purpose];
  const posted = p.status === 'POSTED';
  const isRefund = p.purpose === 'CUSTOMER_REFUND' || p.purpose === 'SUPPLIER_REFUND';
  const hasCredit = posted && !isRefund && Number(p.unallocated) > 0;
  const activeAllocations = p.allocations.filter((a) => !a.reversed);

  return (
    <>
      <BackLink to="/payments" label="Payments" />
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ mb: 3, justifyContent: 'space-between', alignItems: { md: 'flex-end' } }}
      >
        <Box>
          <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="h3">{p.number}</Typography>
            <StatusChip status={p.status} label={posted ? 'Posted' : 'Voided'} />
            <PurposeChip purpose={p.purpose} />
          </Stack>
          <Typography color="text.secondary" sx={{ mt: 0.75 }}>
            {meta.label} ·{' '}
            <Link
              component={RouterLink}
              to={links.contact(p.contact.id, meta.party)}
              underline="hover"
              sx={{ fontWeight: 600 }}
            >
              {p.contact.name}
            </Link>{' '}
            · {businessDate(p.paymentDate, format)}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PrintRounded />}
            onClick={() => void navigate(`/payments/${p.id}/print`)}
          >
            Print
          </Button>
          <WhatsappA4
            print={false}
            type="payment"
            id={p.id}
            number={p.number}
            contact={p.contact}
            vars={{ amount: money(p.amount, format), date: businessDate(p.paymentDate, format) }}
            doc={() => paymentDoc(p, format)}
          />
          {posted && !isRefund && can('payments.allocate') && (
            <Button
              variant="outlined"
              startIcon={<SwapHorizRounded />}
              onClick={() => setDialog('reallocate')}
            >
              Reallocate
            </Button>
          )}
          {hasCredit && can('payments.create', 'payments.refund') && (
            <Button
              variant="outlined"
              startIcon={<CurrencyExchangeRounded />}
              onClick={() => setDialog('refund')}
            >
              Refund credit
            </Button>
          )}
          {posted && can('payments.void') && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<BlockRounded />}
              onClick={() => setDialog('void')}
            >
              Void
            </Button>
          )}
          <MoreMenu
            linked={linkedAllocations(p.allocations)}
            exportModel="payment"
            exportId={p.id}
          />
        </Stack>
      </Stack>

      {!posted && (
        <Alert severity="error" variant="outlined" sx={{ mb: 2.5 }}>
          Voided {dateTime(p.voidedAt, format)}
          {p.voidReason ? ` — “${p.voidReason}”` : ''}. Its journal entry and allocations were
          reversed; the documents it paid are open again.
        </Alert>
      )}

      <StatGrid>
        <StatCard
          label="Amount"
          value={<Money value={p.amount} />}
          hint={p.direction === 'IN' ? 'Money in' : 'Money out'}
          icon={<AccountBalanceWalletRounded />}
          tone="primary"
        />
        <StatCard
          label={isRefund ? 'Credit paid out' : 'Allocated'}
          value={<Money value={p.allocated} />}
          hint={
            <Box sx={{ mt: 0.75 }}>
              <AllocationBar value={percent(p.allocated, p.amount)} />
            </Box>
          }
          icon={<CheckCircleRounded />}
          tone="success"
        />
        <StatCard
          label={isRefund ? 'Not matched' : 'Credit on account'}
          value={<Money value={p.unallocated} />}
          hint={Number(p.unallocated) > 0 ? 'Can settle future documents' : 'Nothing left over'}
          icon={<SavingsRounded />}
          tone={Number(p.unallocated) > 0 ? 'warning' : 'neutral'}
        />
        <StatCard
          label="Method"
          value={p.method.name}
          hint={`${METHOD_KINDS[p.method.kind]} · ${p.account.code} ${p.account.name}`}
          icon={METHOD_ICONS[p.method.kind]}
          tone="info"
        />
      </StatGrid>

      <Box
        sx={{
          display: 'grid',
          gap: 2.5,
          gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: '1.6fr 1fr' },
          alignItems: 'start',
        }}
      >
        <SectionCard
          flush
          title={isRefund ? 'Credit paid out' : 'What it settled'}
          subtitle={
            isRefund
              ? 'The credits this refund paid out, oldest first'
              : `${String(activeAllocations.length)} document${activeAllocations.length === 1 ? '' : 's'}`
          }
        >
          <AllocationsTable
            allocations={p.allocations}
            selfId={p.id}
            emptyText={
              isRefund
                ? 'No credits matched.'
                : 'Not allocated yet — the full amount is credit on account.'
            }
          />
        </SectionCard>
        <SectionCard title="Details">
          <InfoGrid
            items={[
              { label: 'Party', value: p.contact.name },
              { label: 'Phone', value: p.contact.phone ?? '—' },
              { label: 'Date', value: businessDate(p.paymentDate, format) },
              { label: 'Reference', value: p.reference ?? '—' },
              { label: 'Account', value: `${p.account.code} · ${p.account.name}` },
              { label: 'Recorded', value: dateTime(p.createdAt, format) },
            ]}
          />
          {p.notes && (
            <Box sx={{ mt: 2.5, p: 1.75, borderRadius: '10px', bgcolor: 'action.hover' }}>
              <Typography variant="caption" color="text.secondary">
                Notes
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {p.notes}
              </Typography>
            </Box>
          )}
        </SectionCard>
      </Box>

      <ReasonDialog
        open={dialog === 'void'}
        onClose={() => setDialog(null)}
        title={`Void ${p.number}?`}
        confirmLabel="Void payment"
        body={
          <>
            This posts an exact reversing entry for <b>{money(p.amount, format)}</b>
            {activeAllocations.length > 0 && (
              <>
                {' '}
                and re-opens the {activeAllocations.length} document
                {activeAllocations.length === 1 ? '' : 's'} it settled
              </>
            )}
            . {p.contact.name}’s balance goes back to what it was before. This can’t be undone.
          </>
        }
        onConfirm={(reason) => voidMut.mutateAsync(reason)}
      />
      {posted && !isRefund && (
        <ReallocateDialog
          open={dialog === 'reallocate'}
          onClose={() => setDialog(null)}
          contactId={p.contact.id}
          ledger={meta.ledger}
          credit={{ type: 'PAYMENT', id: p.id, number: p.number }}
          allocations={p.allocations}
          unallocated={p.unallocated}
        />
      )}
      {hasCredit && (
        <RefundDialog
          open={dialog === 'refund'}
          onClose={() => setDialog(null)}
          contact={p.contact}
          party={meta.party}
        />
      )}
      <AttachmentsPanel recordType="PAYMENT" recordId={p.id} sx={{ mt: 3 }} />
      <WhatsappHistory documentType="payment" documentId={p.id} sx={{ mt: 3 }} />
      <EmailHistory documentType="payment" documentId={p.id} sx={{ mt: 3 }} />
    </>
  );
}
